import React, { useState } from "react";
import api, { formatError } from "@/lib/api";
import { X, Send, KeyRound, FileText, FolderLock } from "lucide-react";
import { toast } from "@/components/Toaster";

/**
 * Modal a user opens from inside a room to ask an App Owner for scoped access
 * to the *specific* document or folder they're looking at. Pre-fills room_id
 * (always) and doc_id (when known) so the Owner can grant scoped perms in
 * one click without guessing.
 *
 * Props:
 *  - roomId    (required) — current room context
 *  - docId     (optional) — when set, scopes the request to a single doc/folder
 *  - docName   (optional) — display label for the scoped doc/folder
 *  - isFolder  (optional) — toggles icon + copy ("folder" vs "document")
 *  - onClose   () => void — close the modal
 *  - onDone    () => void — fired on successful submit (parent can refresh)
 */
export default function RequestAccessModal({
  roomId, docId, docName, isFolder = false, onClose, onDone,
}) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (!reason.trim()) { setErr("Please describe what you need."); return; }
    setBusy(true); setErr("");
    try {
      await api.post("/access-requests", {
        room_id: roomId,
        doc_id: docId || null,
        reason: reason.trim(),
      });
      toast.success("Request sent. An App Owner will review it shortly.");
      onDone?.();
    } catch (e) {
      setErr(formatError(e));
    } finally {
      setBusy(false);
    }
  };

  const ScopeIcon = isFolder ? FolderLock : FileText;

  return (
    <div className="fixed inset-0 bg-black/60 z-[70] flex items-center justify-center p-6" onClick={onClose}>
      <form
        onSubmit={submit}
        onClick={(e) => e.stopPropagation()}
        className="bg-card border border-border rounded-md w-full max-w-md p-6 relative"
        data-testid="request-access-modal"
      >
        <button type="button" onClick={onClose} aria-label="Close"
          className="absolute top-3 right-3 p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-secondary transition">
          <X className="w-4 h-4" />
        </button>
        <div className="flex items-center gap-2 label-mono text-muted-foreground">
          <KeyRound className="w-3 h-3" /> Request access
        </div>
        <h3 className="mt-2 font-display text-xl tracking-tight">
          {docName ? "Need more access here?" : "Request access to this room"}
        </h3>

        {docName && (
          <div className="mt-4 flex items-start gap-2 p-3 rounded-md border border-emerald-500/30 bg-emerald-500/[0.06]">
            <ScopeIcon className="w-4 h-4 mt-0.5 text-emerald-500 flex-shrink-0" />
            <div className="min-w-0 text-sm">
              <div className="label-mono text-muted-foreground">Scoped to</div>
              <div className="font-medium truncate">{docName}</div>
              <div className="text-xs text-muted-foreground mt-0.5">
                An Owner will grant permissions scoped to this {isFolder ? "folder" : "document"} only.
              </div>
            </div>
          </div>
        )}

        <div className="mt-4">
          <label className="label-mono">Reason</label>
          <textarea
            value={reason} onChange={(e) => setReason(e.target.value)}
            rows={4}
            data-testid="request-access-reason"
            placeholder={docName
              ? `Why do you need access to "${docName}"? Owners use this to pick the right perms.`
              : "What do you need to do in this room?"}
            className="mt-1 w-full px-3 py-2 bg-background border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-ring text-sm"
            required
          />
        </div>

        {err && <div className="mt-4 text-sm text-destructive">{err}</div>}

        <div className="mt-6 flex gap-2 justify-end">
          <button type="button" onClick={onClose}
            className="px-4 py-2 text-sm rounded-md border border-border hover:bg-secondary transition">
            Cancel
          </button>
          <button
            type="submit" disabled={busy}
            data-testid="request-access-submit"
            className="px-4 py-2 text-sm rounded-md bg-primary text-primary-foreground hover:bg-primary/90 transition disabled:opacity-50 inline-flex items-center gap-1.5"
          >
            <Send className="w-3.5 h-3.5" /> {busy ? "Sending…" : "Send request"}
          </button>
        </div>
      </form>
    </div>
  );
}
